<template>
    <ContentWrapper>
        
        <div class="bg-white/50  border border-gray-300 rounded-tl-4xl rounded-br-4xl">
            <div class="flex flex-col flex-col-reverse md:flex-row items-center justify-center w-full gap-8 md:gap-0">
                <div class="w-full md:w-1/2 p-8">

                    <div class="">
                        <div class="text-secondary-500 text-sm uppercase font-bold -mt-4 mb-8">Platform Overview</div>
                        <FeatureStep v-reveal subheading="Prepare" :isSelectable="false" :icon="GlobeAltIcon"
                            title="A single, intuitive CMS to <strong class='text-accent-500'>setup</strong> your events">
                            <template #description>
                                <p>Your central command for building and launching every part of your event including
                                    <strong>Emails, Websites,
                                        Registration Forms, Apps, Portals</strong>, and more.
                                </p>
                                <p class="mt-4">All event data lives in one system, keeping <strong>Schedules, Companies
                                        and
                                        Attendees</strong> seamlessly connected.</p>
                                <p class="mt-4">Granular <strong>Access and Permissions</strong>, live
                                    <strong>reporting</strong>, and complete <strong>visibility</strong> - before,
                                    during and after your event.
                                </p>
                            </template>
                        </FeatureStep>

                        <FeatureStep v-reveal subheading="Engage" :isSelectable="false"
                            :icon="DevicePhoneMobileIcon"
                            title="Beautiful, fully featured apps to <strong class='text-primary-500'>engage</strong> your attendees">
                            <template #description>

                                <p>One <strong>app</strong> experience, delivered across <strong>iOS, Android and web</strong> - with built-in <strong>portals</strong> for
                                    delegates, speakers, exhibitors and staff.</p>
                                <p class="mt-4">An intuitive <strong>content and navigation</strong> system to design event experiences - from <strong>session and speaker pages</strong> to <strong>interactive maps, company profiles and documents</strong>.
                                </p>
                                <p class="mt-4">Jam-packed with features to keep your attendees engaged and informed. <strong>Live polling, Q&A, push notifications, messaging, meetings, activity feed, lead capture, personalised agendas, notes, bookmarks</strong> and more.</p>
                            </template>
                        </FeatureStep>

                        <FeatureStep v-reveal subheading="Manage" :isSelectable="false" :icon="CubeTransparentIcon" title="Onsite Management & Hardware to <strong class='text-accent-500'>run</strong> your event">

                            <template #description>
                                <p>Run your event with fully integrated onsite management and hardware, all seamlessly connected to your registration and app experience.</p>
                                <p class="mt-4">Deliver live <strong>session engagement</strong> with <strong>Q&A and polling</strong>, facilitated in real time. Print <strong>badges</strong> via self-service <strong>kiosks</strong>, staff-assisted counters or hybrid setups - fast and reliable.</p>
                                <p class="mt-4">Track <strong>session attendance</strong> through staff scanning or directly from the app. <strong>Manage attendees</strong> on the spot, resetting access, updating details and permissions instantly.</p>                                
                            </template>
                        </FeatureStep>

                        <FeatureStep v-reveal="500" subheading="Secure" :isSelectable="false" :icon="ShieldCheckIcon" title="Secure, Stable and Scalable to <strong class='text-primary-500'>trust</strong> the delivery">

                            <template #description>
                                <p>Great events start with great data. Entegy is built to capture, connect and manage, without duplication or silos.</p>
                                <p class="mt-4">Through a single CMS, integrated portals and apps, and powerful APIs, exhibitors, speakers, attendees and sponsors can securely access and update their own information - keeping everything accurate, current and in sync.</p>
                                <p class="mt-4">One true source of truth, so when you arrive onsite, every list, badge, session and screen runs from the same reliable, secure, stable data.</p>
                            </template>
                        </FeatureStep>

                        <div class="text-center pt-6">
                            <TextLink text="View full overview of features" link="/overview" />
                        </div>
                    </div>
                </div>
                <div class="w-full md:w-1/2 bg-gradient-to-br from-white to-slate-300">
                    <div class="">
                        <div class="grid gap-4 grid-cols-2 w-full">
                            <div :class="sceneBackgrounds[currentSceneIndex]" v-reveal
                                class="rounded-bl-4xl aspect-video col-span-2 relative overflow-hidden transition-colors duration-800">
                                <DeviceWrapper :deviceType="currentScene[0].deviceType" :width="95"
                                    :x="currentScene[0].x" :y="currentScene[0].y" :z="currentScene[0].z"
                                    :imageSrc="currentScene[0].imageSrc" />
                            </div>

                            <div :class="sceneBackgrounds[currentSceneIndex]" v-reveal
                                class="rounded-br-4xl rounded-tl-4xl col-span-1 row-span-2 aspect-phone relative overflow-hidden transition-colors duration-800">
                                <DeviceWrapper :deviceType="currentScene[1].deviceType" :width="currentScene[1].width"
                                    :x="currentScene[1].x" :y="currentScene[1].y" :z="currentScene[1].z"
                                    :imageSrc="currentScene[1].imageSrc" />
                            </div>

                            <div :class="sceneBackgrounds[currentSceneIndex]" v-reveal
                                class="rounded-tl-4xl aspect-square relative overflow-hidden transition-colors duration-800">
                                <ImageWrapper :imageSrc="currentScene[3].imageSrc" :aspectRatio="'1/1'" />
                            </div>

                            <div :class="sceneBackgrounds[currentSceneIndex]" v-reveal
                                class="rounded-bl-4xl row-span-2 aspect-phone transition-colors duration-800 relative overflow-hidden">
                                <DeviceWrapper :deviceType="currentScene[2].deviceType" :width="currentScene[2].width"
                                    :x="currentScene[2].x" :y="currentScene[2].y" :z="currentScene[2].z"
                                    :imageSrc="currentScene[2].imageSrc" />

                            </div>
                            <div :class="sceneBackgrounds[currentSceneIndex]" v-reveal
                                class="rounded-tl-4xl aspect-square transition-colors duration-800 relative overflow-hidden">
                                <ImageWrapper :imageSrc="currentScene[4].imageSrc" :aspectRatio="'1/1'" />
                            </div>
                            <div :class="sceneBackgrounds[currentSceneIndex]" v-reveal
                                class="rounded-tl-4xl col-span-2 aspect-video transition-colors duration-800">
                                <BadgeAndKiosk :sceneIndex="currentSceneIndex" />
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>

    </ContentWrapper>
</template>

<script setup>

import { ref, computed } from 'vue';
import DeviceWrapper from './DeviceWrapper.vue';
import ImageWrapper from './ImageWrapper.vue';

import cms1 from '@/images/screenshots/cms/app-setup-menu.png';
import cms2 from '@/images/screenshots/cms/profile-edit.png';
import cms3 from '@/images/screenshots/cms/website-builder.png';

import app1menu from '@/images/screenshots/app/theme-1/menu.png';
import app1other from '@/images/screenshots/app/theme-1/profile-meeting.png';

import app2menu from '@/images/screenshots/app/theme-2/menu.png';
import app2other from '@/images/screenshots/app/theme-2/schedule.png';

import app3menu from '@/images/screenshots/app/theme-3/menu.png';
import app3other from '@/images/screenshots/app/theme-3/session.png';

import website1 from '@/images/screenshots/web/theme-1-web.png';
import website2 from '@/images/screenshots/web/theme-2-web.png';
import website3 from '@/images/screenshots/web/theme-3-web.png';

import registration1 from '@/images/screenshots/web/theme-1-registration.png';
import registration2 from '@/images/screenshots/web/theme-2-registration.png';
import registration3 from '@/images/screenshots/web/theme-3-registration.png';

import {
    DevicePhoneMobileIcon,
    GlobeAltIcon,
    CubeTransparentIcon,
    ShieldCheckIcon
} from '@heroicons/vue/24/outline'

import TextLink from './Buttons/TextLink.vue';
import ContentWrapper from './Layouts/ContentWrapper.vue'
import SectionHeader from './Layouts/SectionHeader.vue'
import FeatureStep from './Layouts/FeatureStep.vue'
import BadgeAndKiosk from './BadgeAndKiosk.vue';

const ETSY_PLACEHOLDER = 'https://i.etsystatic.com/10459395/r/il/c41aa9/5946258820/il_570xN.5946258820_2023.jpg';

const selectedFeatureIndex = ref(0);


const sceneBackgrounds = [
    "bg-secondary-500",
    "bg-primary-800",
    "bg-slate-900"
]

const sceneOne = ref([
    {
        deviceType: 'laptop',
        x: 0,
        y: 10,
        z: 0,
        imageSrc: cms1,
        width: 95
    },
    {
        deviceType: 'phone',
        x: 0,
        y: 15,
        z: 0,
        imageSrc: app1menu,
        width: 100
    },
    {
        deviceType: 'phone',
        x: 3,
        y: -15,
        z: 1,
        imageSrc: app1other,
        width: 90
    },
    {
        imageSrc: registration1,
    },
    {
        imageSrc: website1,
    }
])

const sceneTwo = ref([
    {
        deviceType: 'laptop',
        x: 0,
        y: -15,
        z: 0,
        imageSrc: cms2,
        width: 90
    },
    {
        deviceType: 'phone',
        x: 0,
        y: -21,
        z: 5,
        imageSrc: app2menu,
        width: 82
    },
    {
        deviceType: 'phone',
        x: 5,
        y: 18,
        z: -5,
        imageSrc: app2other,
        width: 95
    },
    {
        imageSrc: registration2,
    },
    {
        imageSrc: website2,
    }
])

const sceneThree = ref([
    {
        deviceType: 'laptop',
        x: 0,
        y: 0,
        z: 0,
        imageSrc: cms3,
        width: 90
    },
    {
        deviceType: 'phone',
        x: 0,
        y: -11,
        z: 8,
        imageSrc: app3menu,
        width: 95
    },
    {
        deviceType: 'phone',
        x: -4,
        y: -2,
        z: 5,
        imageSrc: app3other,
        width: 78
    },
    {
        imageSrc: registration3,
    },
    {
        imageSrc: website3,
    }
])

const currentSceneIndex = ref(0);
const currentScene = computed(() => {
    return currentSceneIndex.value === 0 ? sceneOne.value : currentSceneIndex.value === 1 ? sceneTwo.value : sceneThree.value;
})

import { onMounted, onUnmounted } from 'vue'

let intervalId = null

onMounted(() => {
    intervalId = setInterval(() => {
        currentSceneIndex.value = (currentSceneIndex.value + 1) % 3;
    }, 3000);
});

onUnmounted(() => {
    if (intervalId) clearInterval(intervalId);
});

</script>

<style scoped>
.aspect-phone {
    aspect-ratio: 9/16;
}
</style>